import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { getUserDetails, trackEvent } from '@surfline/web-common';
import theme from '@wavetrak/theme';

import { useDeviceSize } from 'hooks/useDeviceSize';
import { getSpotDetails } from 'selectors/spot';
import { useAppSelector } from 'stores/hooks';
import type { SpotDetails } from 'types/spot';
import type { Camera } from 'types/camera';
import getSubscriptionEntitlement from 'utils/getSubscriptionEntitlement';
import { useUserPermissionStatus } from 'selectors/user';

interface Args {
  multiCamLoaded: boolean;
  primaryCam: Camera;
  multiCamViewActive: boolean;
}

const useTrackCamChanges = ({ multiCamLoaded, primaryCam, multiCamViewActive }: Args) => {
  const spot: SpotDetails = useAppSelector(getSpotDetails);
  const { hasPremiumCamPermissions } = useUserPermissionStatus();
  const userDetails = useAppSelector(getUserDetails);

  const subscriptionEntitlement = getSubscriptionEntitlement(hasPremiumCamPermissions, userDetails);

  const router = useRouter();
  const { spotId } = router.query;

  const device = useDeviceSize();
  /* istanbul ignore next */
  const isDesktop = device.width >= theme.breakpoints.values.tabletLg;

  useEffect(() => {
    if (multiCamLoaded) {
      trackEvent('Toggled Cam View', {
        category: 'cams & reports',
        subregionId: spot.subregion._id,
        spotId,
        camID: primaryCam._id,
        camName: primaryCam.title,
        isMobileView: !isDesktop,
        view: multiCamViewActive ? 'Multi' : 'Single',
        cam_number: spot.cameras.length,
        subscriptionEntitlement,
        lineupCam: !!primaryCam.isLineupCam,
      });
    }
    // We only want to track an event when the user changes the cam view or the primary cam
    // so we'll disable this lint rule.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryCam, multiCamViewActive]);
};

export default useTrackCamChanges;
